import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../Icon'
import { PackingList, Receipt } from './PrintSheet'
import { formatPrice } from '../../../shared/money'
import {
  createShow, getShow, listPlants, listShows, markSale, refundSale, selectForShow,
  sell, setPacked, showSummary, unselect, updateShow,
  type Plant, type Sale, type Show, type ShowItem, type Summary,
} from '../../lib/ppc'

/**
 * Show — select, pack, sell, close.
 *
 * ── The sell button can be pressed twice ──────────────────────────────────────
 *
 * It will be. A phone at a market stall with one bar of signal is exactly where
 * a slow request gets tapped again. The key generated below is created ONCE per
 * basket and sent with the request; the server's unique index turns the second
 * attempt into a no-op that returns the first sale. The button is also disabled
 * while in flight, but that is the courtesy, not the guarantee — a disabled
 * button does nothing about the tab that was reloaded mid-request.
 */
type Tab = 'select' | 'pack' | 'sell' | 'close'

export default function ShowScreen({ canWrite }: { canWrite: boolean }) {
  const [shows, setShows] = useState<Show[]>([])
  const [showId, setShowId] = useState<string | null>(null)
  const [items, setItems] = useState<ShowItem[]>([])
  const [sales, setSales] = useState<Sale[]>([])
  const [tab, setTab] = useState<Tab>('select')
  const [summary, setSummary] = useState<Summary | null>(null)
  const [error, setError] = useState<string | null>(null)
  /*
   * What is being printed, if anything. Rendered into the page and hidden on
   * screen; the print stylesheet hides everything else. Cleared as soon as the
   * dialog has been handed the page, so the app is not left in a print mode
   * somebody has to escape from.
   */
  const [printing, setPrinting] = useState<{ kind: 'packing' } | { kind: 'receipt'; sale: Sale } | null>(null)
  const [adding, setAdding] = useState(false)

  const load = useCallback(async (id: string) => {
    const { show, items: got, sales: made } = await getShow(id)
    setItems(got)
    setSales(made)
    setShows((all) => all.map((s) => (s.id === id ? { ...s, ...show } : s)))
  }, [])

  useEffect(() => {
    void listShows().then((all) => {
      setShows(all)
      if (all.length && !showId) setShowId(all[0]!.id)
    }).catch(() => {})
  }, [showId])

  useEffect(() => {
    if (showId) void load(showId).catch(() => {})
  }, [showId, load])

  useEffect(() => {
    if (showId && tab === 'close') void showSummary(showId).then(setSummary).catch(() => {})
  }, [showId, tab, sales])

  const show = shows.find((s) => s.id === showId) ?? null

  if (!show) {
    return (
      <>
        <div className="pagehead">
          <div>
            <p className="eyebrow">Pack light. Sell simply.</p>
            <h1>Your show, in your pocket.</h1>
            <p>Pick from your collection. Sell from this list.</p>
          </div>
        </div>
        <NewShow canWrite={canWrite} onCreated={(created) => { setShows([created]); setShowId(created.id) }} />
      </>
    )
  }

  return (
    <>
      <div className="pagehead">
        <div>
          <p className="eyebrow">Pack light. Sell simply.</p>
          <h1>{show.name}</h1>
          <p>Pick from your collection. Sell from this list.</p>
        </div>
        <div className="actions row">
          {shows.length > 1 && (
            <select value={showId ?? ''} onChange={(e) => setShowId(e.target.value)} aria-label="Which show">
              {shows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <span className={`chip ${show.status === 'closed' ? '' : 'ok'}`}>{show.status}</span>
          {/*
            * There was no way to set up a SECOND show. The empty state offered
            * it once and then never again, so the first show was permanent —
            * found by the end-to-end suite, which could not run twice.
            */}
          {canWrite && (
            <button type="button" onClick={() => setAdding(true)}>
              <Icon name="plus" size={15} /> New show
            </button>
          )}
        </div>
      </div>

      {adding && (
        <NewShow
          canWrite={canWrite}
          onCreated={(created) => {
            setShows((all) => [created, ...all])
            setShowId(created.id)
            setAdding(false)
            setTab('select')
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <nav className="tabrow">
        {(['select', 'pack', 'sell', 'close'] as Tab[]).map((key) => (
          <button key={key} type="button" aria-current={tab === key} onClick={() => setTab(key)}>
            {key === 'select' ? 'Select' : key === 'pack' ? 'Packing list' : key === 'sell' ? 'Sell' : 'Close show'}
          </button>
        ))}
      </nav>

      {error && <p className="error">{error}</p>}

      {tab === 'select' && (
        <Select showId={show.id} items={items} canWrite={canWrite} onChanged={() => void load(show.id)} />
      )}

      {tab === 'pack' && (
        <div className="card">
          <div className="row" style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0, marginRight: 'auto' }}><Icon name="clipboard" size={18} /> Packing list</h3>
            {items.length > 0 && (
              <button type="button" onClick={() => setPrinting({ kind: 'packing' })}>
                <Icon name="clipboard" size={15} /> Print
              </button>
            )}
          </div>
          {items.length === 0 && <p className="muted">Nothing selected yet.</p>}
          <ul className="task-list">
            {items.map((item) => (
              <li key={item.id} className={item.packed ? 'done' : undefined}>
                <label className="checkbox">
                  <input
                    type="checkbox" checked={!!item.packed} disabled={!canWrite}
                    onChange={async (e) => {
                      const packed = e.target.checked
                      /*
                       * Tick it NOW, then tell the server.
                       *
                       * The checkbox is controlled by server state, so without
                       * this it springs back until the round trip finishes —
                       * at a market stall on one bar that is a visible flicker
                       * on every plant, and the natural response is to tap it
                       * again, which unticks it.
                       *
                       * The reload afterwards is still the truth; this only
                       * covers the gap.
                       */
                      setItems((all) => all.map((i) => (i.id === item.id ? { ...i, packed: packed ? 1 : 0 } : i)))
                      try {
                        await setPacked(show.id, item.id, packed)
                      } finally {
                        await load(show.id)
                      }
                    }}
                  />
                  <span>
                    <b>{item.nickname || item.code}</b>
                    <span className="muted small">{item.code} · {formatPrice(item.asking_price_cents)}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="muted small">{items.filter((i) => i.packed).length} of {items.length} packed.</p>
        </div>
      )}

      {tab === 'sell' && (
        <Sell
          showId={show.id} items={items} canWrite={canWrite}
          onSold={() => void load(show.id)} onError={setError}
          sales={sales}
          onSaleChanged={() => void load(show.id)}
          onPrintReceipt={(sale) => setPrinting({ kind: 'receipt', sale })}
        />
      )}

      {printing?.kind === 'packing' && (
        <PackingList show={show} items={items} onDone={() => setPrinting(null)} />
      )}
      {printing?.kind === 'receipt' && (
        <Receipt show={show} sale={printing.sale} onDone={() => setPrinting(null)} />
      )}

      {tab === 'close' && summary && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Close-show summary</h3>
          <dl className="pairs">
            <dt>Units sold</dt><dd>{summary.unitsSold}</dd>
            <dt>Units remaining</dt><dd>{summary.unitsRemaining}</dd>
            <dt>Gross sales</dt><dd>{formatPrice(summary.totals.gross_cents)}</dd>
            <dt>Discounts</dt><dd>{formatPrice(summary.totals.discount_cents)}</dd>
            <dt>Refunds</dt><dd>{formatPrice(summary.totals.refund_cents)}</dd>
            <dt>Net</dt><dd><b>{formatPrice(summary.totals.net_cents - summary.totals.refund_cents)}</b></dd>
          </dl>

          <h4>By payment method</h4>
          <ul className="small">
            {summary.byMethod.map((row) => (
              <li key={`${row.method}-${row.status}`}>
                {row.method === 'ath' ? 'ATH Business' : 'Cash'} · {row.status} · {row.sales} sale{row.sales === 1 ? '' : 's'} · {formatPrice(row.total_cents)}
              </li>
            ))}
          </ul>

          {canWrite && show.status !== 'closed' && (
            <button type="button" onClick={async () => { await updateShow(show.id, { status: 'closed' }); await load(show.id) }}>
              Close this show
            </button>
          )}
        </div>
      )}
    </>
  )
}

function NewShow({
  canWrite, onCreated, onCancel,
}: {
  canWrite: boolean
  onCreated: (show: Show) => void
  /** Present when this is an ADDITIONAL show, so it opens straight at the form. */
  onCancel?: () => void
}) {
  const [name, setName] = useState('')
  const [naming, setNaming] = useState(!!onCancel)

  if (!canWrite) {
    return (
      <div className="card empty">
        <Icon name="bag" size={30} />
        <h3>Give the first show a date.</h3>
        <p>Your full collection stays here. Only the plants you select go on the packing list.</p>
      </div>
    )
  }

  if (!naming) {
    return (
      <div className="card empty">
        <Icon name="bag" size={30} />
        <h3>Give the first show a date.</h3>
        <p>Your full collection stays here. Only the plants you select go on the packing list.</p>
        <button type="button" className="primary" onClick={() => setNaming(true)}>Create your first show</button>
      </div>
    )
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Create a show</h3>
      <label>
        What is it called?
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Spring plant show" autoFocus
        />
      </label>
      <div className="row">
        <button
          type="button" className="primary" disabled={!name.trim()}
          onClick={async () => onCreated(await createShow({ name: name.trim() }))}
        >
          Create it
        </button>
        <button type="button" onClick={() => (onCancel ? onCancel() : setNaming(false))}>Cancel</button>
      </div>
    </div>
  )
}

function Select({
  showId, items, canWrite, onChanged,
}: { showId: string; items: ShowItem[]; canWrite: boolean; onChanged: () => void }) {
  const [plants, setPlants] = useState<Plant[]>([])
  const chosen = useMemo(() => new Set(items.map((i) => i.plant_id)), [items])

  useEffect(() => {
    void listPlants({ availability: 'available' }).then(setPlants).catch(() => {})
  }, [items])

  return (
    <div className="card">
      <h3>Choose what goes</h3>
      <p className="muted small">
        Selecting does not copy anything — a price changed later still reaches the tag.
      </p>
      <ul className="plant-list">
        {plants.map((plant) => {
          const item = items.find((i) => i.plant_id === plant.id)
          return (
            <li key={plant.id}>
              <label className="checkbox row">
                <input
                  type="checkbox" checked={chosen.has(plant.id)} disabled={!canWrite}
                  onChange={async (e) => {
                    if (e.target.checked) await selectForShow(showId, [plant.id])
                    else if (item) await unselect(showId, item.id)
                    onChanged()
                  }}
                />
                <span className="plant-main">
                  <b>{plant.nickname || plant.code}</b>
                  <span className="muted small">{plant.code} · {formatPrice(plant.asking_price_cents)}</span>
                </span>
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function Sell({
  showId, items, canWrite, sales, onSold, onError, onSaleChanged, onPrintReceipt,
}: {
  showId: string
  items: ShowItem[]
  canWrite: boolean
  sales: Sale[]
  onSold: () => void
  onError: (message: string | null) => void
  onSaleChanged: () => void
  onPrintReceipt: (sale: Sale) => void
}) {
  const [basket, setBasket] = useState<Record<string, number>>({})
  const [method, setMethod] = useState<'cash' | 'ath'>('cash')
  const [discount, setDiscount] = useState('')
  const [selling, setSelling] = useState(false)

  /*
   * One key per basket, minted when the basket becomes non-empty and retired
   * when the sale lands. Regenerating it per render would defeat the guard
   * entirely — every retry would look like a new sale.
   */
  const key = useRef<string>(crypto.randomUUID())

  const lines = Object.entries(basket).filter(([, quantity]) => quantity > 0)
  const subtotal = lines.reduce((sum, [plantId, quantity]) => {
    const item = items.find((i) => i.plant_id === plantId)
    return sum + (item?.asking_price_cents ?? 0) * quantity
  }, 0)

  const unpriced = lines.filter(([plantId]) =>
    items.find((i) => i.plant_id === plantId)?.asking_price_cents === null)

  async function complete() {
    if (lines.length === 0) return
    setSelling(true)
    onError(null)
    try {
      await sell(showId, {
        idempotencyKey: key.current,
        method,
        discount,
        lines: lines.map(([plantId, quantity]) => ({ plantId, quantity })),
      })
      setBasket({})
      setDiscount('')
      key.current = crypto.randomUUID()
      onSold()
    } catch (problem) {
      onError(problem instanceof Error ? problem.message : 'That sale did not go through.')
    } finally {
      setSelling(false)
    }
  }

  const available = items.filter((i) => i.availability === 'available' || i.is_batch)

  return (
    <>
      <div className="card">
        <h3 style={{ marginTop: 0 }}><Icon name="dollar" size={18} /> Sell</h3>
        {available.length === 0 && <p className="muted">Nothing left to sell from this show.</p>}
        <ul className="plant-list">
          {available.map((item) => (
            <li key={item.id}>
              <span className="plant-main">
                <b>{item.nickname || item.code}</b>
                <span className="muted small">
                  {item.code} · {formatPrice(item.asking_price_cents)}
                  {item.is_batch ? ` · ${item.stock} left` : ''}
                </span>
              </span>
              <span className="plant-meta">
                {item.is_batch ? (
                  <input
                    type="text" inputMode="numeric" aria-label={`How many ${item.code}`}
                    style={{ width: 64 }} value={basket[item.plant_id] ?? ''}
                    onChange={(e) => setBasket({ ...basket, [item.plant_id]: Number(e.target.value) || 0 })}
                  />
                ) : (
                  <label className="checkbox">
                    <input
                      type="checkbox" checked={!!basket[item.plant_id]} disabled={!canWrite}
                      onChange={(e) => setBasket({ ...basket, [item.plant_id]: e.target.checked ? 1 : 0 })}
                    />
                    <span className="sr-only">Add {item.code}</span>
                  </label>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {lines.length > 0 && (
        <div className="card">
          <h3>Basket</h3>
          <dl className="pairs">
            <dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd>
          </dl>

          {unpriced.length > 0 && (
            <p className="error small">
              Something in the basket has no asking price. Set one before selling it — it will not be sold for nothing.
            </p>
          )}

          <label>
            Discount
            <input type="text" inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" />
          </label>

          <div className="row">
            <button type="button" aria-current={method === 'cash'} onClick={() => setMethod('cash')}><Icon name="cash" size={15} /> Cash</button>
            <button type="button" aria-current={method === 'ath'} onClick={() => setMethod('ath')}><Icon name="phone" size={15} /> ATH Business</button>
          </div>

          <button
            type="button" className="primary" disabled={selling || !canWrite || unpriced.length > 0}
            onClick={() => void complete()}
          >
            {selling ? 'Recording…' : 'Complete sale'}
          </button>

          {method === 'ath' && (
            <p className="muted small">
              This records the sale as <b>awaiting payment</b>. Check ATH Business on your phone,
              then mark it paid below. Awaiting is not paid.
            </p>
          )}
        </div>
      )}

      {sales.length > 0 && (
        <div className="card">
          <h3>Sales so far</h3>
          <ul className="task-list">
            {sales.map((sale) => (
              <li key={sale.id}>
                <span>
                  <b>{formatPrice(sale.total_cents)}</b>{' '}
                  <span className={`chip status-${sale.status}`}>
                    {sale.status === 'awaiting' ? 'awaiting payment' : sale.status}
                  </span>
                  <span className="muted small">
                    {sale.method === 'ath' ? 'ATH' : 'Cash'}
                    {sale.refund_cents > 0 ? ` · refunded ${formatPrice(sale.refund_cents)}` : ''}
                  </span>
                </span>
                <span className="row">
                  <button type="button" onClick={() => onPrintReceipt(sale)}>
                    <Icon name="clipboard" size={14} /> Receipt
                  </button>
                </span>
                {canWrite && (
                  <span className="row">
                    {sale.status === 'awaiting' && (
                      <button type="button" onClick={async () => { await markSale(sale.id, 'paid'); onSaleChanged() }}>
                        <Icon name="check" size={14} /> Mark paid
                      </button>
                    )}
                    {sale.status === 'paid' && (
                      <RefundControl sale={sale} onDone={onSaleChanged} />
                    )}
                    {sale.status !== 'void' && sale.status !== 'refunded' && (
                      <button type="button" className="danger" onClick={async () => { await markSale(sale.id, 'void'); onSaleChanged() }}>
                        Void
                      </button>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}


/**
 * Refunding, in part or in full.
 *
 * "Refund" on its own means refund what is left, which is what the word means
 * at a stall. A PART refund needs a number, and needed somewhere to type it:
 * the API has always supported partial refunds and the screen only ever sent
 * the whole amount, so a customer returning one plant out of three could not
 * be handled without voiding the entire sale.
 *
 * The amount is a STRING until it is sent, like every other money field here —
 * see shared/money.ts for why.
 */
function RefundControl({ sale, onDone }: { sale: Sale; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const left = sale.total_cents - sale.refund_cents

  async function refund(value: string) {
    setBusy(true)
    setProblem(null)
    try {
      await refundSale(sale.id, value)
      setOpen(false)
      setAmount('')
      onDone()
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'That refund did not go through.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return <button type="button" onClick={() => setOpen(true)}>Refund</button>
  }

  return (
    <span className="row">
      <input
        type="text" inputMode="decimal" value={amount} style={{ width: 110, marginTop: 0 }}
        placeholder={`All of ${formatPrice(left)}`} aria-label="Refund amount"
        onChange={(e) => setAmount(e.target.value)}
      />
      <button type="button" className="primary" disabled={busy} onClick={() => void refund(amount)}>
        {busy ? 'Refunding…' : amount.trim() ? `Refund ${amount.trim()}` : 'Refund it all'}
      </button>
      <button type="button" onClick={() => { setOpen(false); setProblem(null) }}>Cancel</button>
      {problem && <span className="error small">{problem}</span>}
    </span>
  )
}
